import React from "react";
import { motion } from "framer-motion";
import { Shield, Sparkles, Star, Award, ChevronRight, Map, Compass, Blocks } from "lucide-react";

const FloatingParticle = ({ delay, x, y, size = 4 }) => (
  <motion.div
    className="absolute rounded-full pointer-events-none"
    style={{
      width: size,
      height: size,
      left: `${x}%`,
      top: `${y}%`,
      background: "linear-gradient(135deg, #60a5fa, #818cf8)",
    }}
    initial={{ opacity: 0, scale: 0 }}
    animate={{
      opacity: [0, 0.6, 0],
      scale: [0, 1, 0],
      y: [0, -30, -60],
    }}
    transition={{
      duration: 3,
      delay,
      repeat: Infinity,
      ease: "easeOut",
    }}
  />
);

const FellowsIntroSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs — blue scheme */}
      <div
        className="absolute top-[-200px] left-[-100px] w-[700px] h-[700px] rounded-full blur-[140px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #c4b5fd 50%, #93c5fd 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div
        className="absolute top-1/3 right-1/4 w-[400px] h-[400px] rounded-full blur-[120px] opacity-15 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #818cf8 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      {/* Floating particles */}
      <FloatingParticle delay={0} x={20} y={30} size={5} />
      <FloatingParticle delay={0.8} x={75} y={60} size={4} />
      <FloatingParticle delay={1.6} x={40} y={70} size={6} />
      <FloatingParticle delay={2.2} x={60} y={25} size={3} />
      <FloatingParticle delay={0.4} x={85} y={45} size={5} />
      <FloatingParticle delay={1.2} x={15} y={55} size={4} />

      <div className="w-full flex gap-12 items-center justify-center relative z-10 max-w-[1240px]">
        {/* Left Side: Content */}
        <div className="flex-1 flex flex-col items-start max-w-[580px]">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-[44px] lg:text-[50px] font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-6 text-left"
          >
            AI Innovation{" "}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-500 to-blue-600 bg-clip-text text-transparent block mt-1">
              Fellows Program
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="text-lg text-slate-500 max-w-lg mb-10 leading-relaxed font-medium text-left"
          >
            Join a cohort of top dentists from across the country, led by the CMO of the MSU Health System.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100/60 mb-8"
          >
            <span className="text-[22px] font-extrabold text-blue-600">650+</span>
            <span className="text-[14px] font-semibold text-slate-600">applicants so far</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="flex flex-col gap-6 mb-10 w-full"
          >
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0 mt-1 shadow-sm border border-blue-200">
                <Map className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="text-[17px] font-bold text-slate-900 mb-1.5">Shape the Future of AI in Dentistry</h4>
                <p className="text-[14px] text-slate-500 leading-relaxed font-medium">
                  Your clinical insights directly influence which AI features our engineering team prioritizes.
                  You'll get the opportunity participate in roadmap reviews, get early beta access, and test new tools before anyone else.
                </p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0 mt-1 shadow-sm border border-indigo-200">
                <Compass className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h4 className="text-[17px] font-bold text-slate-900 mb-1.5">Lead AI Transformation</h4>
                <p className="text-[14px] text-slate-500 leading-relaxed font-medium">
                  Establish yourself as a thought leader in AI transformation. Advise other offices and create community content around how to maximize value using AI.
                </p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center shrink-0 mt-1 shadow-sm border border-sky-200">
                <Blocks className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <h4 className="text-[17px] font-bold text-slate-900 mb-1.5">Launch Your Own AI Agents</h4>
                <p className="text-[14px] text-slate-500 leading-relaxed font-medium">
                  Use our AI Clinician Creator Suite to launch your own AI agents in our marketplace. Whenever other offices use them, you'll receive a revenue share and your work will receive citations.
                </p>
              </div>
            </div>
          </motion.div>

        </div>

        {/* Right Side: Image */}
        <motion.div
          initial={{ opacity: 0, x: 30, scale: 0.95 }}
          whileInView={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.7, ease: "easeOut" }}
          className="flex-1 relative flex justify-end"
        >
          {/* Subtle glow behind image */}
          <div className="absolute inset-0 bg-blue-500/20 blur-[60px] rounded-full translate-x-4 translate-y-4 max-w-[500px] ml-auto" />
          
          <div className="relative rounded-[32px] overflow-hidden border-[8px] border-white/60 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.15)] bg-white max-w-[550px] w-full">
            {/* Small decorative corner elements */}
            <div className="absolute top-5 right-5 w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl z-10 flex items-center justify-center border border-white/30 shadow-sm">
              <Shield className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            
            <img 
              src="/assets/docs.jpg" 
              alt="Innovation Fellows" 
              className="w-full h-auto aspect-[3/4] object-cover object-[center_20%]"
            />

            {/* Gradient overlay at bottom of image for depth */}
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-slate-900/60 to-transparent pointer-events-none" />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FellowsIntroSlide;
